import { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useTranslation } from "react-i18next";

const RISK_KEYS = {
  1: "riskLevels.veryLow",
  2: "riskLevels.low",
  3: "riskLevels.medium",
  4: "riskLevels.high",
  5: "riskLevels.veryHigh",
};

const getRiskChipColor = (riskLevel) => {
  const colors = {
    1: "#4caf50",
    2: "#8bc34a",
    3: "#ffeb3b",
    4: "#ff9800",
    5: "#f44336",
  };
  return colors[riskLevel] || "default";
};

export default function PreviewTable({
  users,
  loading,
  selectedIds,
  setSelectedIds,
}) {
  const { t } = useTranslation("export");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const columns = [
    {
      field: "subject_id",
      headerName: t("preview.columns.subjectId"),
      flex: 1,
      minWidth: 120,
    },
    {
      field: "sex",
      headerName: t("preview.columns.sex"),
      width: 100,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "age",
      headerName: t("preview.columns.age"),
      width: 80,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "latest_risk_level",
      headerName: t("preview.columns.riskLevel"),
      width: 120,
      renderCell: (params) => {
        const riskLevel = params.value;
        if (!riskLevel) return "-";
        return (
          <Chip
            label={RISK_KEYS[riskLevel] ? t(RISK_KEYS[riskLevel]) : "-"}
            size="small"
            sx={{
              bgcolor: getRiskChipColor(riskLevel),
              color: riskLevel === 3 ? "black" : "white",
            }}
          />
        );
      },
    },
    {
      field: "total_recommendations",
      headerName: t("preview.columns.totalRecs"),
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "pending_recommendations",
      headerName: t("preview.columns.pendingRecs"),
      width: 100,
      align: "center",
      headerAlign: "center",
    },
  ];

  const handleSelectionChange = (newSelectionModel) => {
    setSelectedIds(newSelectionModel);
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("preview.title")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("preview.count", { count: users.length })}
          {selectedIds.length > 0 && ` | ${t("preview.selected", { count: selectedIds.length })}`}
        </Typography>
      </Box>

      <DataGrid
          rows={users}
          columns={columns}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={handleSelectionChange}
          pagination
          paginationModel={{
            pageSize: rowsPerPage,
            page: page,
          }}
          onPaginationModelChange={({ page, pageSize }) => {
            setPage(page);
            setRowsPerPage(pageSize);
          }}
          pageSizeOptions={[10, 25, 50]}
          autoHeight
          hideFooter
          sx={{
            "& .MuiDataGrid-row:hover": {
              bgcolor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        />

      {/* Custom Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mt: 2,
          gap: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("preview.rowsPerPage", { defaultValue: "Rows per page:" })}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 70 }}>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
          {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, users.length)} of {users.length}
        </Typography>

        <IconButton
          onClick={() => setPage(0)}
          disabled={page === 0}
          size="small"
        >
          <FirstPageIcon />
        </IconButton>
        <IconButton
          onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
          disabled={page === 0}
          size="small"
        >
          <NavigateBeforeIcon />
        </IconButton>
        <IconButton
          onClick={() => setPage((prev) => Math.min(prev + 1, Math.ceil(users.length / rowsPerPage) - 1))}
          disabled={page >= Math.ceil(users.length / rowsPerPage) - 1}
          size="small"
        >
          <NavigateNextIcon />
        </IconButton>
        <IconButton
          onClick={() => setPage(Math.ceil(users.length / rowsPerPage) - 1)}
          disabled={page >= Math.ceil(users.length / rowsPerPage) - 1}
          size="small"
        >
          <LastPageIcon />
        </IconButton>
      </Box>
    </Paper>
  );
}
